import {useState, useCallback, useMemo} from "react";
import {
	GridFilterModel,
	GridSortModel,
	GridInitialState,
	GridCallbackDetails
} from "@mui/x-data-grid-pro";
import {
	useListCourses,
	CoursesControllerListParams,
	CoursesControllerListSortBy,
	CourseAttributeFilterDto,
	CourseAttributeFilterDtoField,
	DEFAULT_COURSES_LIST_PAGE_SIZE
} from "@/app2/shared/data";
import {getFromLocalStorage} from "@/utils/common-utilities";

const SORT_FIELDS: readonly string[] = Object.values(CoursesControllerListSortBy);

const SORT_STORAGE_KEY = "coursesSortSettings";

interface PersistedSortModel {
	field: string;
	sort: "asc" | "desc";
}

function getPersistedSort(): PersistedSortModel | null {
	return getFromLocalStorage<PersistedSortModel>(SORT_STORAGE_KEY) ?? null;
}

function persistSort(model: PersistedSortModel): void {
	localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(model));
}

const FILTERABLE_FIELDS: readonly string[] = [
	"title",
	"description",
	"slug",
	"contentType",
	"privacy",
	"createdAt",
	"updatedAt",
	"publishedAt",
	"enrollmentCount",
	"moduleCount",
	"isBlacklisted",
	"authorCohort"
];

interface useCoursesListGridProps {
	initialFilterSettings: CoursesControllerListParams;
	onFilterModelChange?: (filterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => void;
}

export const useCoursesListGrid = ({initialFilterSettings, onFilterModelChange}: useCoursesListGridProps) => {
	const persistedSort = getPersistedSort();
	const [filterModel, setFilterModel] = useState<CoursesControllerListParams>(() => ({
		...initialFilterSettings,
		sortBy: persistedSort?.field as CoursesControllerListSortBy ?? initialFilterSettings.sortBy,
		sortDirection: persistedSort?.sort ?? initialFilterSettings.sortDirection
	}));

	const {data, isLoading, refetch} = useListCourses(filterModel);

	const courses = data?.data ?? [];
	const rowCount = data?.totalCount ?? 0;

	const onPageChange = useCallback((page: number) => {
		setFilterModel((prev) => ({
			...prev,
			page: page + 1
		}));
	}, []);

	const onPageSizeChange = useCallback((pageSize: number) => {
		setFilterModel((prev) => ({
			...prev,
			pageSize,
			page: 1
		}));
	}, []);

	const onFilterChange = useCallback((newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
		setFilterModel((prev) => {
			const filters: CourseAttributeFilterDto[] = (newFilterModel.items ?? [])
				.filter((item: { columnField?: string; field?: string; value?: unknown; operatorValue?: string; operator?: string }) => {
					const field = item.columnField ?? item.field;
					const hasValue = item.value !== undefined && item.value !== null && String(item.value).trim() !== "";
					const operator = item.operatorValue ?? item.operator ?? "co";
					return field && FILTERABLE_FIELDS.includes(field) && (operator === "pr" || hasValue);
				})
				.map((item: { columnField?: string; field?: string; value?: unknown; operatorValue?: string; operator?: string }) => {
					const field = (item.columnField ?? item.field) as CourseAttributeFilterDtoField;
					const operator = (item.operatorValue ?? item.operator ?? "co") as CourseAttributeFilterDto["operator"];
					let value: string | undefined;
					if (operator === "pr") {
						value = undefined;
					} else if (item.value instanceof Date) {
						value = item.value.toISOString();
					} else {
						value = String(item.value ?? "");
					}
					return { field, value, operator };
				});

			onFilterModelChange && onFilterModelChange(newFilterModel, details);

			return {
				...prev,
				page: 1,
				filters: filters.length > 0 ? { operator: newFilterModel.linkOperator, conditions: filters } : undefined
			};
		});
	}, []);

	const onSortModelChange = useCallback((newSortModel: GridSortModel) => {
		const field = newSortModel.length > 0 && SORT_FIELDS.includes(newSortModel[0].field)
			? newSortModel[0].field as CoursesControllerListSortBy
			: "updatedAt";
		const sort = newSortModel.length > 0 ? (newSortModel[0].sort as "asc" | "desc") : "desc";

		persistSort({field, sort});

		setFilterModel((prev) => ({
			...prev,
			sortBy: field,
			sortDirection: sort
		}));
	}, []);

	const sortModel = useMemo<GridSortModel>(
		() => [
			{
				field: filterModel.sortBy ?? "updatedAt",
				sort: filterModel.sortDirection ?? "desc"
			}
		],
		[filterModel.sortBy, filterModel.sortDirection]
	);

	const defaultInitialState: GridInitialState = {
		pagination: {
			pageSize: filterModel.pageSize ?? DEFAULT_COURSES_LIST_PAGE_SIZE,
			page: (filterModel.page ?? 1) - 1
		},
		sorting: {
			sortModel: [{field: filterModel.sortBy ?? "updatedAt", sort: filterModel.sortDirection ?? "desc"}]
		},
		columns: {
			columnVisibilityModel: {
				updatedAt: false
			}
		}
	};

	return {
		courses,
		rowCount,
		isLoading,
		refetch,
		page: (filterModel.page ?? 1) - 1,
		pageSize: filterModel.pageSize ?? DEFAULT_COURSES_LIST_PAGE_SIZE,
		sortModel,
		onPageChange,
		onPageSizeChange,
		onFilterChange,
		onSortModelChange,
		defaultInitialState
	};
};
